import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { ToastData } from 'shared/types/toast';
import { UilTimes } from '@iconscout/react-unicons';
import Logo from './Logo';

interface StepXToastProps {
  toast: ToastData;
  onClose: () => void;
}

const StepXToast: React.FC<StepXToastProps> = ({ toast, onClose }) => {
  const [isLeaving, setIsLeaving] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const autoHideTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleClose = useCallback(() => {
    if (isLeaving) return; // Prevent multiple close calls
    
    setIsLeaving(true);
    
    // Clear any existing timers
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
    
    // Remove toast after animation completes
    setTimeout(() => {
      onClose();
    }, 400); // Match CSS animation duration
  }, [onClose, isLeaving]);

  // Auto hide timer
  useEffect(() => {
    if (!toast.duration || toast.duration <= 0) return;

    autoHideTimerRef.current = setTimeout(() => {
      handleClose();
    }, toast.duration);

    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
        autoHideTimerRef.current = null;
      }
    };
  }, [toast.duration, handleClose]);

  const toastClasses = classNames(
    'stepx-toast-item',
    {
      'stepx-toast-open': !isLeaving,
      'stepx-toast-leave': isLeaving,
    }
  );

  return (
    <div className={toastClasses} onClick={toast.onClick}>
      <div className="stepx-toast-content">
        <div className="stepx-toast-icon">
          {toast.icon ||  <Logo text={false} width={40} className="fw-bolder fs-5 d-inline-block" />}
        </div>
        
        <div className="stepx-toast-body">
          {toast.title && (
            <div className="stepx-toast-title">
              {toast.title}
            </div>
          )}
          {toast.description && (
            <div className="stepx-toast-description">
              {toast.description}
            </div>
          )}
        </div>
        
        {toast.closeButton !== false && (
          <button 
            className="stepx-toast-close-button" 
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            aria-label="Close toast"
          >
            <UilTimes size={18} />
          </button>
        )}
      </div>
      
      {toast.showProgress && toast.duration && toast.duration > 0 && (
        <div 
          ref={progressRef} 
          className="stepx-toast-progress-bar" 
          style={{ animationDuration: `${toast.duration}ms` }} 
        />
      )}
    </div>
  );
};

export default StepXToast;
